import { useGetCurrentUserQuery } from '@/api/authApi';
import oidcConfig from '@/config/oidc';
import { selectUserValidated } from '@/features/auth/authSelector';
import { clearToken, setToken } from '@/features/auth/authSlice';
import { clearSentryUser } from '@/utils/sentry';
import { ExclamationCircleOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { OidcProvider, useOidc, useOidcAccessToken, useOidcUser } from '@axa-fr/react-oidc';
import { Button, Result, Spin } from 'antd';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const AuthContext = createContext(null);

const customSpinIndicator = <LoadingOutlined style={{ fontSize: 24, color: '#ef4444' }} spin />;

const LoadingComponent = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin indicator={customSpinIndicator} />
  </div>
);

// Error component for authentication errors
const AuthErrorComponent = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <Result
      status="error"
      icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
      title="Authentication Error"
      subTitle="Unable to connect to the authentication server. Please check your connection and try again."
      extra={[
        <Button
          type="primary"
          key="retry"
          icon={<ReloadOutlined />}
          onClick={() => window.location.reload()}
        >
          Try Again
        </Button>,
        <Button key="home" onClick={() => (window.location.href = '/')}>
          Go to Home
        </Button>,
      ]}
    />
  </div>
);

// Session lost component - auto redirect to login
const SessionLostComponent = () => {
  useEffect(() => {
    // Auto redirect to login when session expires
    window.location.href = '/login';
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <Spin indicator={customSpinIndicator} />
    </div>
  );
};

// Inner provider that has access to OIDC hooks
const AuthProviderInner = ({ children }) => {
  const { isAuthenticated, login, logout: oidcLogout } = useOidc();
  const { accessToken } = useOidcAccessToken();
  const { oidcUser } = useOidcUser();
  const dispatch = useDispatch();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [tokenStored, setTokenStored] = useState(false);

  // Get userValidated from Redux store
  const userValidated = useSelector(selectUserValidated);

  // Store the access token in Redux when authenticated - must happen before API call
  useEffect(() => {
    if (isAuthenticated && accessToken) {
      dispatch(setToken(accessToken));
      setTokenStored(true);
    } else {
      setTokenStored(false);
    }
  }, [isAuthenticated, accessToken, dispatch]);

  // Use RTK Query to fetch user info from backend after SSO authentication
  // Only call after token is stored in Redux (so baseQuery can read it)
  const {
    isLoading: isValidatingUser,
    isError: userValidationFailed,
    error: validationErrorData,
  } = useGetCurrentUserQuery(undefined, {
    // Only call API when token is stored in Redux and not logging out
    skip: !isAuthenticated || !accessToken || !tokenStored || isLoggingOut,
  });

  // Handle user validation failure - show error
  useEffect(() => {
    if (userValidationFailed && !isLoggingOut && validationErrorData) {
      const errorMessage =
        validationErrorData?.data?.errorDetails ||
        validationErrorData?.data?.message ||
        validationErrorData?.data?.error ||
        validationErrorData?.error ||
        'User not found in the system';
      console.error('User validation failed:', errorMessage);
      setValidationError(errorMessage);
    }
  }, [userValidationFailed, isLoggingOut, validationErrorData]);

  // Clear state on logout
  useEffect(() => {
    if (!isAuthenticated && !isLoggingOut) {
      dispatch(clearToken());
      clearSentryUser();
    }
  }, [isAuthenticated, isLoggingOut, dispatch]);

  // Wrap logout to show loading overlay
  const logout = useCallback(() => {
    setIsLoggingOut(true);
    oidcLogout();
  }, [oidcLogout]);

  const getToken = useCallback(() => {
    return accessToken || null;
  }, [accessToken]);

  const value = useMemo(
    () => ({
      initialized: userValidated || !isAuthenticated,
      isAuthenticated: isAuthenticated && userValidated,
      login,
      logout,
      getToken,
      user: oidcUser,
      isLoggingOut,
    }),
    [isAuthenticated, userValidated, login, logout, getToken, oidcUser, isLoggingOut]
  );

  // Handle logout after viewing error
  const handleLogoutAfterError = useCallback(() => {
    setValidationError(null);
    oidcLogout();
  }, [oidcLogout]);

  // Show error page when user validation fails
  if (validationError) {
    return (
      <AuthContext.Provider value={value}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <Result
            status="error"
            icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
            title="Access Denied"
            subTitle={validationError}
            extra={[
              <Button type="primary" key="login" onClick={handleLogoutAfterError}>
                Back to Login
              </Button>,
            ]}
          />
        </div>
      </AuthContext.Provider>
    );
  }

  // Show loading overlay during logout or user validation
  // Also show loading when authenticated but user not yet validated (API call in progress)
  // Don't show loading if validation already failed (userValidationFailed)
  const showLoading =
    isLoggingOut ||
    isValidatingUser ||
    (isAuthenticated && tokenStored && !userValidated && !validationError && !userValidationFailed);

  // Debug logging
  // console.log('Auth state:', {
  //   isAuthenticated,
  //   tokenStored,
  //   userValidated,
  //   isValidatingUser,
  //   userValidationFailed,
  //   validationError,
  //   isLoggingOut,
  //   showLoading,
  //   accessToken: !!accessToken,
  // });

  if (showLoading) {
    return (
      <AuthContext.Provider value={value}>
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.9)',
            zIndex: 9999,
          }}
        >
          <Spin indicator={customSpinIndicator} />
        </div>
      </AuthContext.Provider>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Handle OIDC events - redirect to /loading after successful token acquisition
const handleOidcEvent = (_configurationName, eventName) => {
  if (eventName === 'token_acquired') {
    // Only redirect if not already on /loading
    if (window.location.pathname !== '/loading') {
      window.location.replace('/loading');
    }
  }
};

// Main provider component
const KeycloakProvider = ({ children }) => {
  return (
    <OidcProvider
      configuration={oidcConfig}
      loadingComponent={LoadingComponent}
      authenticatingComponent={LoadingComponent}
      authenticatingErrorComponent={AuthErrorComponent}
      callbackSuccessComponent={LoadingComponent}
      sessionLostComponent={SessionLostComponent}
      onEvent={handleOidcEvent}
    >
      <AuthProviderInner>{children}</AuthProviderInner>
    </OidcProvider>
  );
};

export const useKeycloak = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useKeycloak must be used within a KeycloakProvider');
  }
  return context;
};

// Alias for clarity
export const useAuth = useKeycloak;

export default KeycloakProvider;
